/* eslint-disable no-console */
/**
 * Structured Logger with PII Redaction and Trace IDs
 * Replaces console.* calls with proper structured logging
 *
 * @example
 * // Component usage with injected logger
 * function MyComponent({ logger }: { logger: Logger }) {
 *   logger.info('User action', { userId: '123' });
 * }
 *
 * // Hook usage
 * const logger = useLogger('MyHook');
 * logger.debug('State updated', { count });
 */

import { v4 as uuidv4 } from 'uuid';
import { captureConsentedException } from './sentry-loader';

// ============================================================================
// Types
// ============================================================================

export type LogLevel = 'trace' | 'debug' | 'info' | 'warn' | 'error';

export interface LogContext {
  traceId?: string;
  userId?: string;
  sessionId?: string;
  component?: string;
  [key: string]: unknown;
}

export interface LogEntry {
  timestamp: string;
  level: LogLevel;
  context: string;
  message: string;
  traceId: string;
  data?: Record<string, unknown>;
  error?: Error;
}

export interface LoggerConfig {
  minLevel: LogLevel;
  enableConsole: boolean;
  enableRemote: boolean;
  redactPII: boolean;
}

interface NormalizedErrorPayload {
  error?: Error;
  extraData?: Record<string, unknown>;
}

// ============================================================================
// PII Detection & Redaction
// ============================================================================

const PII_PATTERNS = {
  email: /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g,
  phone: /\b\d{3}[-.]?\d{3}[-.]?\d{4}\b/g,
  ssn: /\b\d{3}-\d{2}-\d{4}\b/g,
  creditCard: /\b\d{4}[\s-]?\d{4}[\s-]?\d{4}[\s-]?\d{4}\b/g,
  ipv4: /\b(?:\d{1,3}\.){3}\d{1,3}\b/g,
  // German ID numbers
  taxId: /\b\d{11}\b/g,
  // Generic token patterns
  token: /\b[A-Za-z0-9_-]{32,}\b/g,
  // JWT tokens
  jwt: /\beyJ[A-Za-z0-9_-]*\.[A-Za-z0-9_-]*\.[A-Za-z0-9_-]*/g,
};

const SENSITIVE_KEYS = new Set([
  'password',
  'token',
  'secret',
  'api_key',
  'apikey',
  'access_token',
  'accesstoken',
  'refresh_token',
  'refreshtoken',
  'private_key',
  'privatekey',
  'ssn',
  'credit_card',
  'creditcard',
  'cvv',
  'pin',
  'authorization',
]);

/**
 * Redact PII from string content
 */
function redactPIIFromString(str: string): string {
  let redacted = str;
  redacted = redacted.replace(PII_PATTERNS.email, '[EMAIL_REDACTED]');
  redacted = redacted.replace(PII_PATTERNS.phone, '[PHONE_REDACTED]');
  redacted = redacted.replace(PII_PATTERNS.ssn, '[SSN_REDACTED]');
  redacted = redacted.replace(PII_PATTERNS.creditCard, '[CARD_REDACTED]');
  redacted = redacted.replace(PII_PATTERNS.ipv4, '[IP_REDACTED]');
  redacted = redacted.replace(PII_PATTERNS.jwt, '[JWT_REDACTED]');
  return redacted;
}

/**
 * Redact PII from objects (recursive)
 * @param obj - Object to redact
 * @param depth - Current recursion depth (prevents infinite loops)
 * @param maxDepth - Maximum recursion depth
 */
function redactPIIFromObject(obj: unknown, depth = 0, maxDepth = 10): unknown {
  // Prevent infinite recursion
  if (depth > maxDepth) {
    return '[MAX_DEPTH_REACHED]';
  }
  if (obj === null || obj === undefined) return obj;

  if (typeof obj === 'string') {
    return redactPIIFromString(obj);
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => redactPIIFromObject(item, depth + 1, maxDepth));
  }

  if (typeof obj === 'object') {
    const redacted: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj)) {
      // Redact sensitive keys entirely
      if (SENSITIVE_KEYS.has(key.toLowerCase())) {
        redacted[key] = '[REDACTED]';
      } else {
        redacted[key] = redactPIIFromObject(value, depth + 1, maxDepth);
      }
    }
    return redacted;
  }

  return obj;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function toDataRecord(value: unknown): Record<string, unknown> | undefined {
  if (value === undefined) return undefined;
  if (isRecord(value)) return value;
  return { value };
}

function mergeDataRecords(
  primary?: unknown,
  secondary?: Record<string, unknown>,
): Record<string, unknown> | undefined {
  const primaryRecord = toDataRecord(primary);
  if (primaryRecord && secondary) {
    return { ...secondary, ...primaryRecord };
  }
  return primaryRecord ?? secondary;
}

export function normalizeUnknownError(
  value: unknown,
  fallbackMessage = 'Unknown error',
): NormalizedErrorPayload {
  if (value === undefined) return {};
  if (value instanceof Error) return { error: value };

  if (typeof value === 'string') {
    return { error: new Error(value) };
  }

  if (isRecord(value)) {
    if (value.error instanceof Error) {
      const { error, ...rest } = value;
      return {
        error,
        extraData: Object.keys(rest).length > 0 ? rest : undefined,
      };
    }

    const message =
      typeof value.message === 'string' && value.message.trim().length > 0
        ? value.message
        : typeof value.error === 'string' && value.error.trim().length > 0
          ? value.error
          : null;

    const hasErrorShape =
      message !== null ||
      typeof value.stack === 'string' ||
      typeof value.code === 'string' ||
      typeof value.details === 'string' ||
      typeof value.hint === 'string' ||
      typeof value.name === 'string';

    if (!hasErrorShape) {
      return {
        extraData: value,
      };
    }

    const error = new Error(message ?? fallbackMessage);
    if (typeof value.name === 'string' && value.name.trim().length > 0) {
      error.name = value.name;
    }
    if (typeof value.stack === 'string' && value.stack.trim().length > 0) {
      error.stack = value.stack;
    }

    return {
      error,
      extraData: value,
    };
  }

  return { error: new Error(String(value)) };
}

// ============================================================================
// Logger Class
// ============================================================================

const LOG_LEVELS: Record<LogLevel, number> = {
  trace: -1, // Ultra-verbose level for future needs
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

export class Logger {
  private context: string;
  private config: LoggerConfig;
  private contextData: LogContext;

  constructor(context: string, config: Partial<LoggerConfig> = {}, contextData: LogContext = {}) {
    this.context = context;
    this.contextData = {
      traceId: uuidv4(),
      ...contextData,
    };

    this.config = {
      minLevel: import.meta.env.PROD ? 'warn' : 'debug',
      enableConsole: true,
      enableRemote: import.meta.env.PROD,
      redactPII: true,
      ...config,
    };
  }

  /**
   * Create child logger with additional context
   */
  child(context: string, additionalContext: LogContext = {}): Logger {
    return new Logger(`${this.context}:${context}`, this.config, {
      ...this.contextData,
      ...additionalContext,
    });
  }

  /**
   * Add context data to all subsequent logs
   */
  withContext(contextData: LogContext): Logger {
    return new Logger(this.context, this.config, {
      ...this.contextData,
      ...contextData,
    });
  }

  /**
   * Get the current trace ID
   */
  getTraceId(): string {
    return this.contextData.traceId || '';
  }

  private shouldLog(level: LogLevel): boolean {
    return LOG_LEVELS[level] >= LOG_LEVELS[this.config.minLevel];
  }

  private formatEntry(level: LogLevel, message: string, data?: unknown, error?: Error): LogEntry {
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      context: this.context,
      message,
      traceId: this.contextData.traceId || uuidv4(),
    };

    if (data !== undefined) {
      entry.data = this.config.redactPII
        ? (redactPIIFromObject(data) as Record<string, unknown>)
        : (data as Record<string, unknown>);
    }

    if (error) {
      entry.error = error;
    }

    return entry;
  }

  private write(entry: LogEntry): void {
    if (!this.shouldLog(entry.level)) return;

    // Console output
    if (this.config.enableConsole) {
      const prefix = `[${entry.level.toUpperCase()}][${entry.context}][${entry.traceId.slice(0, 8)}]`;
      // trace and debug use console.log; others use their corresponding console method
      const consoleMethod =
        entry.level === 'trace' || entry.level === 'debug' ? 'log' : entry.level;

      if (entry.error) {
        console[consoleMethod](prefix, entry.message, entry.data || '', entry.error);
      } else if (entry.data) {
        console[consoleMethod](prefix, entry.message, entry.data);
      } else {
        console[consoleMethod](prefix, entry.message);
      }
    }

    // Remote logging via Sentry
    if (this.config.enableRemote && entry.level === 'error' && entry.error) {
      void captureConsentedException(entry.error, {
        extra: {
          context: entry.context,
          traceId: entry.traceId,
          ...entry.data,
        },
      });
    }
  }

  trace(message: string, data?: unknown): void {
    this.write(this.formatEntry('trace', message, data));
  }

  debug(message: string, data?: unknown): void {
    this.write(this.formatEntry('debug', message, data));
  }

  info(message: string, data?: unknown): void {
    this.write(this.formatEntry('info', message, data));
  }

  warn(message: string, data?: unknown): void {
    this.write(this.formatEntry('warn', message, data));
  }

  error(message: string, error?: Error | unknown, data?: unknown): void {
    const normalized = normalizeUnknownError(error, message);
    const mergedData = mergeDataRecords(data, normalized.extraData);
    this.write(this.formatEntry('error', message, mergedData, normalized.error));
  }

  /**
   * Performance timing helper
   */
  perf(operation: string, durationMs: number, data?: unknown): void {
    const emoji = durationMs > 1000 ? '🐌' : durationMs > 100 ? '⏱️' : '⚡';
    this.debug(`${emoji} ${operation}: ${durationMs.toFixed(2)}ms`, data);
  }

  /**
   * Measure async operation performance
   */
  async measure<T>(operation: string, fn: () => Promise<T>, data?: unknown): Promise<T> {
    const start = performance.now();
    try {
      const result = await fn();
      const duration = performance.now() - start;
      this.perf(operation, duration, data);
      return result;
    } catch (error) {
      const duration = performance.now() - start;
      this.error(`${operation} failed after ${duration.toFixed(2)}ms`, error, data);
      throw error;
    }
  }
}

// ============================================================================
// Global Logger Instance (Deprecated - use createLogger instead)
// ============================================================================

let deprecationWarningShown = false;

const globalLogger = new Logger('Global');

/**
 * @deprecated Use createLogger() or inject logger prop instead
 */
export const logger = new Proxy(globalLogger, {
  get(target, prop) {
    if (
      !deprecationWarningShown &&
      typeof prop === 'string' &&
      ['debug', 'info', 'warn', 'error'].includes(prop)
    ) {
      console.warn(
        '[DEPRECATED] Global logger import is deprecated. Use createLogger() or inject logger prop instead.\n' +
          'Example: const logger = createLogger("MyComponent");',
      );
      deprecationWarningShown = true;
    }
    return target[prop as keyof typeof target];
  },
});

// ============================================================================
// Factory Functions
// ============================================================================

/**
 * Create a new logger instance
 */
export function createLogger(
  context: string,
  config?: Partial<LoggerConfig>,
  contextData?: LogContext,
): Logger {
  return new Logger(context, config, contextData);
}

/**
 * Create logger for edge functions (Deno)
 */
export function createEdgeFunctionLogger(functionName: string, request?: Request): Logger {
  const contextData: LogContext = {
    function: functionName,
  };

  if (request) {
    contextData.method = request.method;
    contextData.url = request.url;
  }

  return new Logger(
    `EdgeFunction:${functionName}`,
    {
      enableRemote: true, // Always enable for production edge functions
    },
    contextData,
  );
}
